import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";

import { purchaseApi } from "./api";
import { dateText, errorMessage, money } from "./utils";

import "./ProformaInvoicePage.css";

/* =========================================================
   HELPERS
========================================================= */

const emptyItem = () => ({
  description: "",
  specification: "",
  qty: 1,
  unit: "NOS",
  rate: 0,
});

const initialForm = () => ({
  proformaDate: new Date().toISOString().slice(0, 10),

  vendor: "",

  projectId: "",
  projectName: "",

  company: "SVB",

  status: "Draft",

  items: [emptyItem()],

  discountPercent: 0,
  gstPercent: 18,
  gstType: "CGST_SGST",

  termsAndConditions: "",
  remark: "",
});

const round2 = (value) =>
  Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const getAttachmentName = (file, index = 0) =>
  file?.originalName || file?.fileName || file?.name || `Document ${index + 1}`;

/* =========================================================
   PAGE
========================================================= */

export default function ProformaInvoicePage() {
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);

  const [form, setForm] = useState(initialForm());

  const [editing, setEditing] = useState(null);

  const [attachments, setAttachments] = useState([]);

  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);

  const [search, setSearch] = useState("");

  const [year, setYear] = useState("");
  const [quarter, setQuarter] = useState("");
  const [month, setMonth] = useState("");

  const [error, setError] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /* =========================================================
     LOAD
  ========================================================= */

  const load = async () => {
    try {
      setLoading(true);
      setError("");

      const [proformaResponse, vendorResponse, projectResponse] =
        await Promise.all([
          purchaseApi.proformaInvoices.list(),
          purchaseApi.vendors.list(),
          purchaseApi.projects.list(),
        ]);

      const proformaData = proformaResponse?.data || {};

      const vendorData = vendorResponse?.data || {};

      const projectData = projectResponse?.data;

      setRows(
        Array.isArray(proformaData.proformaInvoices)
          ? proformaData.proformaInvoices
          : Array.isArray(proformaData)
            ? proformaData
            : [],
      );

      setVendors(
        Array.isArray(vendorData.vendors)
          ? vendorData.vendors
          : Array.isArray(vendorData)
            ? vendorData
            : [],
      );

      if (Array.isArray(projectData)) {
        setProjects(projectData);
      } else if (Array.isArray(projectData?.projects)) {
        setProjects(projectData.projects);
      } else if (Array.isArray(projectData?.data)) {
        setProjects(projectData.data);
      } else {
        setProjects([]);
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* =========================================================
     OPTIONS
  ========================================================= */

  const vendorOptions = useMemo(() => {
    return vendors
      .map((vendor) => ({
        value: vendor._id,

        label:
          vendor.vendorName ||
          vendor.name ||
          vendor.companyName ||
          "Unnamed Vendor",

        data: vendor,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [vendors]);

  const projectOptions = useMemo(() => {
    return projects
      .filter((project) => project?._id && project?.name)
      .map((project) => ({
        value: project._id,
        label: project.name,
        data: project,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [projects]);

  /* =========================================================
     DYNAMIC DATE FILTERS
  ========================================================= */

  const filterMeta = useMemo(() => {
    const map = {};

    rows.forEach((row) => {
      if (!row.proformaDate) return;

      const date = new Date(row.proformaDate);

      if (Number.isNaN(date.getTime())) {
        return;
      }

      const y = date.getFullYear();

      const m = date.getMonth() + 1;

      const q = Math.floor(date.getMonth() / 3) + 1;

      if (!map[y]) {
        map[y] = {
          months: new Set(),
          quarters: new Set(),
        };
      }

      map[y].months.add(m);
      map[y].quarters.add(q);
    });

    return Object.keys(map)
      .map(Number)
      .sort((a, b) => b - a)
      .map((y) => ({
        year: y,

        months: Array.from(map[y].months).sort((a, b) => a - b),

        quarters: Array.from(map[y].quarters).sort((a, b) => a - b),
      }));
  }, [rows]);

  const selectedYearFilter = useMemo(() => {
    return filterMeta.find((item) => String(item.year) === String(year));
  }, [filterMeta, year]);

  /* =========================================================
     FILTERED ROWS
  ========================================================= */

  const filteredRows = useMemo(() => {
    const searchValue = String(search || "")
      .trim()
      .toLowerCase();

    return rows.filter((row) => {
      if (searchValue) {
        const searchable = [
          row.proformaInvoiceNo,
          row.vendorName,
          row.projectName,
          row.status,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!searchable.includes(searchValue)) {
          return false;
        }
      }

      if (year || month || quarter) {
        if (!row.proformaDate) {
          return false;
        }

        const date = new Date(row.proformaDate);

        if (Number.isNaN(date.getTime())) {
          return false;
        }

        const rowYear = date.getFullYear();

        const rowMonth = date.getMonth() + 1;

        const rowQuarter = Math.floor(date.getMonth() / 3) + 1;

        if (year && String(rowYear) !== String(year)) {
          return false;
        }

        if (month && String(rowMonth) !== String(month)) {
          return false;
        }

        if (quarter && String(rowQuarter) !== String(quarter)) {
          return false;
        }
      }

      return true;
    });
  }, [rows, search, year, month, quarter]);

  const totalAmount = useMemo(
    () =>
      filteredRows.reduce((sum, row) => sum + Number(row.grandTotal || 0), 0),
    [filteredRows],
  );

  /* =========================================================
     CALCULATION
  ========================================================= */

  const calculation = useMemo(() => {
    const items = Array.isArray(form.items) ? form.items : [];

    const subtotal = round2(
      items.reduce(
        (sum, item) => sum + Number(item.qty || 0) * Number(item.rate || 0),
        0,
      ),
    );

    const discountPercent = Number(form.discountPercent || 0);

    const discountAmount = round2(subtotal * (discountPercent / 100));

    const taxableAmount = round2(Math.max(subtotal - discountAmount, 0));

    const gstPercent = Number(form.gstPercent || 0);

    const gstAmount = round2(taxableAmount * (gstPercent / 100));

    const grandTotal = round2(taxableAmount + gstAmount);

    return {
      subtotal,
      discountAmount,
      taxableAmount,
      gstAmount,
      grandTotal,

      cgstAmount: form.gstType === "CGST_SGST" ? round2(gstAmount / 2) : 0,

      sgstAmount: form.gstType === "CGST_SGST" ? round2(gstAmount / 2) : 0,

      igstAmount: form.gstType === "IGST" ? gstAmount : 0,
    };
  }, [form.items, form.discountPercent, form.gstPercent, form.gstType]);

  /* =========================================================
     CREATE
  ========================================================= */

  const openCreate = () => {
    setError("");

    setEditing(null);

    setAttachments([]);

    setForm(initialForm());

    setOpen(true);
  };

  /* =========================================================
     EDIT
  ========================================================= */

  const edit = async (id) => {
    try {
      setError("");

      const { data } = await purchaseApi.proformaInvoices.get(id);

      const proforma = data?.proformaInvoice || data;

      if (!proforma?._id) {
        throw new Error("Proforma invoice not found");
      }

      setEditing(proforma);

      setAttachments([]);

      setForm({
        proformaDate: proforma.proformaDate
          ? new Date(proforma.proformaDate).toISOString().slice(0, 10)
          : new Date().toISOString().slice(0, 10),

        vendor: proforma.vendor?._id || proforma.vendor || "",

        projectId: proforma.projectId?._id || proforma.projectId || "",

        projectName: proforma.projectName || "",

        company: "SVB",

        status: proforma.status || "Draft",

        items:
          Array.isArray(proforma.items) && proforma.items.length
            ? proforma.items.map((item) => ({
                ...item,

                description: item.description || "",

                specification: item.specification || "",

                qty: Number(item.qty || 0),

                unit: item.unit || "NOS",

                rate: Number(item.rate || 0),
              }))
            : [emptyItem()],

        discountPercent: Number(proforma.discountPercent || 0),

        gstPercent: Number(proforma.gstPercent ?? 18),

        gstType: proforma.gstType || "CGST_SGST",

        termsAndConditions: proforma.termsAndConditions || "",

        remark: proforma.remark || "",
      });

      setOpen(true);
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  /* =========================================================
     CLOSE
  ========================================================= */

  const closeForm = () => {
    if (saving) return;

    setOpen(false);

    setEditing(null);

    setAttachments([]);

    setForm(initialForm());
  };

  /* =========================================================
     CHANGE
  ========================================================= */

  const changeForm = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  /* =========================================================
     VENDOR
  ========================================================= */

  const handleVendorChange = (option) => {
    changeForm("vendor", option?.value || "");
  };

  /* =========================================================
     PROJECT
  ========================================================= */

  const handleProjectChange = (option) => {
    setForm((previous) => ({
      ...previous,

      projectId: option?.value || "",

      projectName: option?.data?.name || option?.label || "",
    }));
  };

  /* =========================================================
     ITEMS
  ========================================================= */

  const updateItem = (index, field, value) => {
    setForm((previous) => {
      const items = [...previous.items];

      items[index] = {
        ...items[index],
        [field]: value,
      };

      return {
        ...previous,
        items,
      };
    });
  };

  const addItem = () => {
    setForm((previous) => ({
      ...previous,

      items: [...previous.items, emptyItem()],
    }));
  };

  const removeItem = (index) => {
    setForm((previous) => {
      if (previous.items.length <= 1) {
        return previous;
      }

      return {
        ...previous,

        items: previous.items.filter((_, i) => i !== index),
      };
    });
  };

  /* =========================================================
     ATTACHMENTS
  ========================================================= */

  const handleAttachmentChange = (e) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) {
      return;
    }

    setAttachments((previous) => [...previous, ...files]);

    e.target.value = "";
  };

  const removeNewAttachment = (index) => {
    setAttachments((previous) =>
      previous.filter((_, fileIndex) => fileIndex !== index),
    );
  };

  /* =========================================================
     OPEN SAVED ATTACHMENT
  ========================================================= */

  const openAttachment = async (file) => {
    try {
      setError("");

      if (!file?.fileName) {
        setError("Document file name not found.");
        return;
      }

      const response = await purchaseApi.proformaInvoices.openFile(
        file.fileName,
      );

      const contentType =
        response.headers?.["content-type"] ||
        file.mimeType ||
        "application/octet-stream";

      const blob = new Blob([response.data], {
        type: contentType,
      });

      const blobUrl = window.URL.createObjectURL(blob);

      const newTab = window.open(blobUrl, "_blank");

      if (!newTab) {
        window.URL.revokeObjectURL(blobUrl);

        setError("Popup blocked. Please allow popups.");

        return;
      }

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch (e) {
      console.error("Open PI attachment:", e);

      setError(errorMessage(e) || "Unable to open document.");
    }
  };

  /* =========================================================
     SAVE
  ========================================================= */

  const save = async (e) => {
    e.preventDefault();

    try {
      setError("");

      if (!form.vendor) {
        setError("Please select vendor.");
        return;
      }

      const validItems = form.items
        .map((item) => ({
          ...item,

          description: String(item.description || "").trim(),

          specification: String(item.specification || "").trim(),

          qty: Number(item.qty || 0),

          rate: Number(item.rate || 0),

          unit: String(item.unit || "NOS").trim() || "NOS",
        }))
        .filter((item) => item.description);

      if (!validItems.length) {
        setError("Please add at least one item.");

        return;
      }

      setSaving(true);

      const data = new FormData();

      data.append("proformaDate", form.proformaDate);

      data.append("vendor", form.vendor);

      if (form.projectId) {
        data.append("projectId", form.projectId);
      }

      data.append("projectName", form.projectName || "");

      data.append("company", "SVB");

      data.append("status", form.status || "Draft");

      data.append("items", JSON.stringify(validItems));

      data.append("discountPercent", String(Number(form.discountPercent || 0)));

      data.append("gstPercent", String(Number(form.gstPercent || 0)));

      data.append("gstType", form.gstType || "CGST_SGST");

      data.append("termsAndConditions", form.termsAndConditions || "");

      data.append("remark", form.remark || "");

      attachments.forEach((file) => {
        data.append("attachments", file);
      });

      if (editing?._id) {
        await purchaseApi.proformaInvoices.update(editing._id, data);
      } else {
        await purchaseApi.proformaInvoices.create(data);
      }

      setOpen(false);

      setEditing(null);

      setAttachments([]);

      setForm(initialForm());

      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     DELETE
  ========================================================= */

  const remove = async (id) => {
    if (!window.confirm("Delete this proforma invoice?")) {
      return;
    }

    try {
      setError("");

      await purchaseApi.proformaInvoices.remove(id);

      await load();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  /* =========================================================
     PREVIEW
  ========================================================= */

  const openPreview = async (row) => {
    try {
      setError("");

      const { data } = await purchaseApi.proformaInvoices.get(row._id);

      setPreview(data?.proformaInvoice || data || row);
    } catch {
      setPreview(row);
    }
  };

  const printProforma = () => {
    window.print();
  };

  /* =========================================================
     FILTER
  ========================================================= */

  const getMonthName = (monthNo) => {
    return new Date(2000, Number(monthNo) - 1, 1).toLocaleString("en-IN", {
      month: "long",
    });
  };

  const clearFilters = () => {
    setSearch("");
    setYear("");
    setQuarter("");
    setMonth("");
  };

  /* =========================================================
     SELECTED VALUES
  ========================================================= */

  const selectedVendor =
    vendorOptions.find(
      (option) => String(option.value) === String(form.vendor),
    ) || null;

  const selectedProject =
    projectOptions.find(
      (option) => String(option.value) === String(form.projectId),
    ) || null;

  /* =========================================================
     PREVIEW CALCULATION
  ========================================================= */

  const previewCalculation = useMemo(() => {
    if (!preview) {
      return null;
    }

    const items = Array.isArray(preview.items) ? preview.items : [];

    const subtotal =
      preview.subtotal != null
        ? Number(preview.subtotal)
        : round2(
            items.reduce(
              (sum, item) =>
                sum + Number(item.qty || 0) * Number(item.rate || 0),
              0,
            ),
          );

    const discountPercent = Number(preview.discountPercent || 0);

    const discountAmount =
      preview.discountAmount != null
        ? Number(preview.discountAmount)
        : round2(subtotal * (discountPercent / 100));

    const taxableAmount =
      preview.taxableAmount != null
        ? Number(preview.taxableAmount)
        : round2(Math.max(subtotal - discountAmount, 0));

    const gstPercent = Number(preview.gstPercent || 0);

    const gstAmount =
      preview.gstAmount != null
        ? Number(preview.gstAmount)
        : round2(taxableAmount * (gstPercent / 100));

    const grandTotal =
      preview.grandTotal != null
        ? Number(preview.grandTotal)
        : round2(taxableAmount + gstAmount);

    return {
      subtotal,

      discountPercent,

      discountAmount,

      taxableAmount,

      gstPercent,

      gstAmount,

      grandTotal,

      cgstAmount:
        preview.cgstAmount != null
          ? Number(preview.cgstAmount)
          : preview.gstType === "CGST_SGST"
            ? round2(gstAmount / 2)
            : 0,

      sgstAmount:
        preview.sgstAmount != null
          ? Number(preview.sgstAmount)
          : preview.gstType === "CGST_SGST"
            ? round2(gstAmount / 2)
            : 0,

      igstAmount:
        preview.igstAmount != null
          ? Number(preview.igstAmount)
          : preview.gstType === "IGST"
            ? gstAmount
            : 0,
    };
  }, [preview]);

  /* =========================================================
     UI
  ========================================================= */

  return (
    <div className="proforma-page">
      {/* HEADER */}

      <div className="proforma-head">
        <div>
          <h1>Proforma Invoice</h1>

          <p>Create and manage purchase proforma invoices.</p>
        </div>

        <button
          type="button"
          className="proforma-btn primary"
          onClick={openCreate}
        >
          + Create PI
        </button>
      </div>

      {error && <div className="proforma-error">{error}</div>}

      {/* FILTER */}

      <div className="proforma-card proforma-filter-card">
        <div className="proforma-filter-row">
          <div className="proforma-field proforma-search">
            <label>Search</label>

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="PI No. / Vendor / Project"
            />
          </div>

          <div className="proforma-field">
            <label>Year</label>

            <select
              value={year}
              onChange={(e) => {
                setYear(e.target.value);

                setMonth("");
                setQuarter("");
              }}
            >
              <option value="">All Years</option>

              {filterMeta.map((item) => (
                <option key={item.year} value={item.year}>
                  {item.year}
                </option>
              ))}
            </select>
          </div>

          <div className="proforma-field">
            <label>Quarter</label>

            <select
              value={quarter}
              disabled={!year}
              onChange={(e) => {
                setQuarter(e.target.value);

                if (e.target.value) {
                  setMonth("");
                }
              }}
            >
              <option value="">All Quarters</option>

              {selectedYearFilter?.quarters?.map((q) => (
                <option key={q} value={q}>
                  Q{q}
                </option>
              ))}
            </select>
          </div>

          <div className="proforma-field">
            <label>Month</label>

            <select
              value={month}
              disabled={!year}
              onChange={(e) => {
                setMonth(e.target.value);

                if (e.target.value) {
                  setQuarter("");
                }
              }}
            >
              <option value="">All Months</option>

              {selectedYearFilter?.months?.map((m) => (
                <option key={m} value={m}>
                  {getMonthName(m)}
                </option>
              ))}
            </select>
          </div>

          <button type="button" className="proforma-btn" onClick={clearFilters}>
            Clear
          </button>
        </div>

        <div className="proforma-summary">
          <div className="proforma-stat">
            <small>Records</small>

            <strong>{filteredRows.length}</strong>
          </div>

          <div className="proforma-stat">
            <small>Total Amount</small>

            <strong>{money(totalAmount)}</strong>
          </div>
        </div>
      </div>

      {/* TABLE */}

      <div className="proforma-card proforma-table-card">
        <div className="proforma-table-wrap">
          <table className="proforma-table">
            <thead>
              <tr>
                <th>PI NO.</th>
                <th>VENDOR</th>
                <th>DATE</th>
                <th>PROJECT</th>
                <th>TOTAL</th>
                <th>STATUS</th>
                <th>DOCUMENT</th>
                <th>ACTION</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="proforma-empty">
                    Loading...
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="proforma-empty">
                    No proforma invoices found.
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row._id}>
                    <td>
                      <b>{row.proformaInvoiceNo || "-"}</b>
                    </td>

                    <td>{row.vendorName || "-"}</td>

                    <td>{dateText(row.proformaDate)}</td>

                    <td>{row.projectName || "-"}</td>

                    <td className="proforma-amount">
                      {money(row.grandTotal || 0)}
                    </td>

                    <td>
                      <span className="proforma-status">
                        {row.status || "Draft"}
                      </span>
                    </td>

                    <td>
                      {row.attachments?.length > 0 ? (
                        <button
                          type="button"
                          className="proforma-document-link"
                          onClick={() => openAttachment(row.attachments[0])}
                        >
                          📎 {row.attachments.length} File
                          {row.attachments.length > 1 ? "s" : ""}
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td>
                      <div className="proforma-actions">
                        <button type="button" onClick={() => edit(row._id)}>
                          Edit
                        </button>

                        <button type="button" onClick={() => openPreview(row)}>
                          Preview / PDF
                        </button>

                        <button
                          type="button"
                          className="danger"
                          onClick={() => remove(row._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* =====================================================
          CREATE / EDIT
      ===================================================== */}

      {open && (
        <div className="proforma-modal-backdrop proforma-no-print">
          <div className="proforma-modal">
            <form onSubmit={save}>
              <div className="proforma-modal-head">
                <div>
                  <h2>
                    {editing
                      ? "Edit Proforma Invoice"
                      : "Create Proforma Invoice"}
                  </h2>

                  <p>Enter purchase proforma invoice details.</p>
                </div>

                <button
                  type="button"
                  className="proforma-modal-close"
                  onClick={closeForm}
                >
                  ×
                </button>
              </div>

              <div className="proforma-modal-body">
                {/* BASIC */}

                <div className="proforma-section">
                  <div className="proforma-section-title">
                    Basic Information
                  </div>

                  <div className="proforma-form-grid">
                    <div className="proforma-form-field">
                      <label>
                        Vendor <span>*</span>
                      </label>

                      <Select
                        options={vendorOptions}
                        value={selectedVendor}
                        onChange={handleVendorChange}
                        placeholder="Search / Select Vendor"
                        isClearable
                        isSearchable
                        classNamePrefix="proforma-select"
                      />
                    </div>

                    <div className="proforma-form-field">
                      <label>
                        Project <small>(Optional)</small>
                      </label>

                      <Select
                        options={projectOptions}
                        value={selectedProject}
                        onChange={handleProjectChange}
                        placeholder="Search / Select Project"
                        isClearable
                        isSearchable
                        noOptionsMessage={() => "No project found"}
                        classNamePrefix="proforma-select"
                      />
                    </div>

                    <div className="proforma-form-field">
                      <label>PI Date</label>

                      <input
                        type="date"
                        value={form.proformaDate}
                        onChange={(e) =>
                          changeForm("proformaDate", e.target.value)
                        }
                      />
                    </div>

                    <div className="proforma-form-field">
                      <label>Company</label>

                      <input
                        value="SVB"
                        readOnly
                        className="proforma-readonly"
                      />
                    </div>

                    <div className="proforma-form-field">
                      <label>Status</label>

                      <select
                        value={form.status}
                        onChange={(e) => changeForm("status", e.target.value)}
                      >
                        <option value="Draft">Draft</option>

                        <option value="Pending">Pending</option>

                        <option value="Approved">Approved</option>

                        <option value="Rejected">Rejected</option>

                        <option value="Converted">Converted</option>

                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* ITEMS */}

                <div className="proforma-section">
                  <div className="proforma-section-head">
                    <div className="proforma-section-title">
                      Proforma Invoice Items
                    </div>

                    <button
                      type="button"
                      className="proforma-btn"
                      onClick={addItem}
                    >
                      + Add Item
                    </button>
                  </div>

                  <div className="proforma-items-wrap">
                    <table className="proforma-items-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Description</th>
                          <th>Specification</th>
                          <th>Qty</th>
                          <th>Unit</th>
                          <th>Rate</th>
                          <th>Amount</th>
                          <th></th>
                        </tr>
                      </thead>

                      <tbody>
                        {form.items.map((item, index) => {
                          const amount =
                            Number(item.qty || 0) * Number(item.rate || 0);

                          return (
                            <tr key={index}>
                              <td>{index + 1}</td>

                              <td>
                                <input
                                  value={item.description}
                                  placeholder="Item / Work description"
                                  onChange={(e) =>
                                    updateItem(
                                      index,
                                      "description",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>

                              <td>
                                <input
                                  value={item.specification}
                                  placeholder="Specification"
                                  onChange={(e) =>
                                    updateItem(
                                      index,
                                      "specification",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>

                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.qty}
                                  onChange={(e) =>
                                    updateItem(index, "qty", e.target.value)
                                  }
                                />
                              </td>

                              <td>
                                <input
                                  value={item.unit}
                                  onChange={(e) =>
                                    updateItem(index, "unit", e.target.value)
                                  }
                                />
                              </td>

                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.rate}
                                  onChange={(e) =>
                                    updateItem(index, "rate", e.target.value)
                                  }
                                />
                              </td>

                              <td className="proforma-item-amount">
                                {money(amount)}
                              </td>

                              <td>
                                <button
                                  type="button"
                                  className="proforma-item-delete"
                                  disabled={form.items.length === 1}
                                  onClick={() => removeItem(index)}
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* BOTTOM */}

                <div className="proforma-bottom-grid">
                  <div>
                    <div className="proforma-section">
                      <div className="proforma-section-title">
                        Additional Details
                      </div>

                      <div className="proforma-form-grid two">
                        <div className="proforma-form-field">
                          <label>Discount %</label>

                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.discountPercent}
                            onChange={(e) =>
                              changeForm("discountPercent", e.target.value)
                            }
                          />
                        </div>

                        <div className="proforma-form-field">
                          <label>GST %</label>

                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.gstPercent}
                            onChange={(e) =>
                              changeForm("gstPercent", e.target.value)
                            }
                          />
                        </div>

                        <div className="proforma-form-field">
                          <label>GST Type</label>

                          <select
                            value={form.gstType}
                            onChange={(e) =>
                              changeForm("gstType", e.target.value)
                            }
                          >
                            <option value="CGST_SGST">CGST + SGST</option>

                            <option value="IGST">IGST</option>
                          </select>
                        </div>
                      </div>

                      <div className="proforma-form-field proforma-textarea-field">
                        <label>Terms & Conditions</label>

                        <textarea
                          rows="4"
                          value={form.termsAndConditions}
                          onChange={(e) =>
                            changeForm("termsAndConditions", e.target.value)
                          }
                          placeholder="Enter terms and conditions..."
                        />
                      </div>

                      <div className="proforma-form-field proforma-textarea-field">
                        <label>Remark</label>

                        <textarea
                          rows="3"
                          value={form.remark}
                          onChange={(e) => changeForm("remark", e.target.value)}
                          placeholder="Remark..."
                        />
                      </div>

                      {/* ATTACHMENTS */}

                      <div className="proforma-attachment-section">
                        <div className="proforma-section-title">
                          Supporting Documents
                        </div>

                        <div className="proforma-upload-box">
                          <input
                            id="proforma-attachments"
                            type="file"
                            multiple
                            hidden
                            accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                            onChange={handleAttachmentChange}
                          />

                          <label
                            htmlFor="proforma-attachments"
                            className="proforma-attach-btn"
                          >
                            + Attach Documents
                          </label>

                          <span className="proforma-upload-help">
                            PDF, JPG, PNG, Word or Excel
                          </span>
                        </div>

                        {/* EXISTING */}

                        {editing?.attachments?.length > 0 && (
                          <div className="proforma-file-list">
                            <div className="proforma-file-heading">
                              Existing Documents
                            </div>

                            {editing.attachments.map((file, index) => (
                              <div
                                className="proforma-file-row"
                                key={file._id || index}
                              >
                                <button
                                  type="button"
                                  className="proforma-file-open"
                                  onClick={() => openAttachment(file)}
                                >
                                  <span>📎</span>

                                  <span className="proforma-file-name">
                                    {getAttachmentName(file, index)}
                                  </span>

                                  <span className="proforma-file-open-text">
                                    Open
                                  </span>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* NEW */}

                        {attachments.length > 0 && (
                          <div className="proforma-file-list">
                            <div className="proforma-file-heading">
                              New Documents
                            </div>

                            {attachments.map((file, index) => (
                              <div
                                className="proforma-file-row"
                                key={`${file.name}-${index}`}
                              >
                                <div className="proforma-file-info">
                                  <span>📎</span>

                                  <div>
                                    <div>{file.name}</div>

                                    <small>
                                      {(file.size / 1024 / 1024).toFixed(2)} MB
                                    </small>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  className="proforma-file-remove"
                                  onClick={() => removeNewAttachment(index)}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* TOTAL */}

                  <div className="proforma-total-box">
                    <h3>PI Summary</h3>

                    <div className="proforma-total-row">
                      <span>Subtotal</span>

                      <strong>{money(calculation.subtotal)}</strong>
                    </div>

                    <div className="proforma-total-row">
                      <span>
                        Discount ({Number(form.discountPercent || 0)}
                        %)
                      </span>

                      <strong>- {money(calculation.discountAmount)}</strong>
                    </div>

                    <div className="proforma-total-row">
                      <span>Taxable Amount</span>

                      <strong>{money(calculation.taxableAmount)}</strong>
                    </div>

                    {form.gstType === "CGST_SGST" ? (
                      <>
                        <div className="proforma-total-row">
                          <span>
                            CGST ({Number(form.gstPercent || 0) / 2}
                            %)
                          </span>

                          <strong>{money(calculation.cgstAmount)}</strong>
                        </div>

                        <div className="proforma-total-row">
                          <span>
                            SGST ({Number(form.gstPercent || 0) / 2}
                            %)
                          </span>

                          <strong>{money(calculation.sgstAmount)}</strong>
                        </div>
                      </>
                    ) : (
                      <div className="proforma-total-row">
                        <span>
                          IGST ({Number(form.gstPercent || 0)}
                          %)
                        </span>

                        <strong>{money(calculation.igstAmount)}</strong>
                      </div>
                    )}

                    <div className="proforma-total-row grand">
                      <span>Grand Total</span>

                      <strong>{money(calculation.grandTotal)}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="proforma-modal-footer">
                <button
                  type="button"
                  className="proforma-btn"
                  onClick={closeForm}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="proforma-btn primary"
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : editing
                      ? "Update Proforma Invoice"
                      : "Save Proforma Invoice"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =====================================================
          A4 PREVIEW
      ===================================================== */}

      {preview && previewCalculation && (
        <div className="proforma-preview-overlay">
          <div className="proforma-preview-shell">
            <div className="proforma-preview-actions proforma-no-print">
              <div>
                <strong>Proforma Invoice Preview</strong>

                <span>{preview.proformaInvoiceNo || ""}</span>
              </div>

              <div className="proforma-preview-action-buttons">
                <button type="button" onClick={printProforma}>
                  Print / Save PDF
                </button>

                <button type="button" onClick={() => setPreview(null)}>
                  Close
                </button>
              </div>
            </div>

            <div className="proforma-a4" id="proforma-print-area">
              {/* HEADER */}

              <div className="proforma-pdf-header">
                <div className="proforma-pdf-brand">
                  <div className="proforma-pdf-logo">SVB</div>

                  <div>
                    <h1>Softvolute Buildcon LLP</h1>

                    <p>Purchase Department</p>
                  </div>
                </div>

                <div className="proforma-pdf-title">
                  <h2>PROFORMA INVOICE</h2>

                  <div>
                    <span>PI No.</span>

                    <strong>{preview.proformaInvoiceNo || "-"}</strong>
                  </div>

                  <div>
                    <span>Date</span>

                    <strong>{dateText(preview.proformaDate)}</strong>
                  </div>
                </div>
              </div>

              <div className="proforma-pdf-accent" />

              {/* INFO */}

              <div className="proforma-pdf-info-grid">
                <div className="proforma-pdf-info-box">
                  <div className="proforma-pdf-info-title">VENDOR DETAILS</div>

                  <h3>
                    {preview.vendorName || preview.vendor?.vendorName || "-"}
                  </h3>

                  {preview.contactPerson && (
                    <p>
                      <b>Contact:</b> {preview.contactPerson}
                    </p>
                  )}

                  {preview.contactNo && (
                    <p>
                      <b>Mobile:</b> {preview.contactNo}
                    </p>
                  )}

                  {preview.gstNo && (
                    <p>
                      <b>GSTIN:</b> {preview.gstNo}
                    </p>
                  )}
                </div>

                <div className="proforma-pdf-info-box">
                  <div className="proforma-pdf-info-title">PI DETAILS</div>

                  <div className="proforma-pdf-meta-row">
                    <span>Project</span>

                    <strong>{preview.projectName || "General"}</strong>
                  </div>

                  <div className="proforma-pdf-meta-row">
                    <span>Company</span>

                    <strong>Softvolute Buildcon LLP</strong>
                  </div>

                  <div className="proforma-pdf-meta-row">
                    <span>Status</span>

                    <strong>{preview.status || "Draft"}</strong>
                  </div>
                </div>
              </div>

              {/* ITEMS */}

              <div className="proforma-pdf-section-title">
                Proforma Invoice Items
              </div>

              <table className="proforma-pdf-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Description</th>
                    <th>Specification</th>
                    <th>Qty</th>
                    <th>Unit</th>
                    <th>Rate</th>
                    <th>Amount</th>
                  </tr>
                </thead>

                <tbody>
                  {(preview.items || []).map((item, index) => {
                    const amount =
                      item.amount != null
                        ? Number(item.amount)
                        : Number(item.qty || 0) * Number(item.rate || 0);

                    return (
                      <tr key={item._id || index}>
                        <td>{index + 1}</td>

                        <td>
                          <strong>{item.description || "-"}</strong>
                        </td>

                        <td>{item.specification || "-"}</td>

                        <td>{item.qty || 0}</td>

                        <td>{item.unit || "-"}</td>

                        <td>{money(item.rate || 0)}</td>

                        <td>
                          <strong>{money(amount)}</strong>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* TOTAL */}

              <div className="proforma-pdf-total-area">
                <div />

                <div className="proforma-pdf-total-card">
                  <div>
                    <span>Subtotal</span>

                    <strong>{money(previewCalculation.subtotal)}</strong>
                  </div>

                  {previewCalculation.discountPercent > 0 && (
                    <div>
                      <span>
                        Discount ({previewCalculation.discountPercent}
                        %)
                      </span>

                      <strong>
                        - {money(previewCalculation.discountAmount)}
                      </strong>
                    </div>
                  )}

                  <div>
                    <span>Taxable Amount</span>

                    <strong>{money(previewCalculation.taxableAmount)}</strong>
                  </div>

                  {preview.gstType === "IGST" ? (
                    <div>
                      <span>
                        IGST ({previewCalculation.gstPercent}
                        %)
                      </span>

                      <strong>{money(previewCalculation.igstAmount)}</strong>
                    </div>
                  ) : (
                    <>
                      <div>
                        <span>
                          CGST ({previewCalculation.gstPercent / 2}
                          %)
                        </span>

                        <strong>{money(previewCalculation.cgstAmount)}</strong>
                      </div>

                      <div>
                        <span>
                          SGST ({previewCalculation.gstPercent / 2}
                          %)
                        </span>

                        <strong>{money(previewCalculation.sgstAmount)}</strong>
                      </div>
                    </>
                  )}

                  <div className="proforma-pdf-grand-total">
                    <span>GRAND TOTAL</span>

                    <strong>{money(previewCalculation.grandTotal)}</strong>
                  </div>
                </div>
              </div>

              {/* TERMS */}

              {(preview.termsAndConditions || preview.remark) && (
                <div className="proforma-pdf-bottom-content">
                  {preview.termsAndConditions && (
                    <div className="proforma-pdf-notes">
                      <h4>Terms & Conditions</h4>

                      <p>{preview.termsAndConditions}</p>
                    </div>
                  )}

                  {preview.remark && (
                    <div className="proforma-pdf-notes">
                      <h4>Remarks</h4>

                      <p>{preview.remark}</p>
                    </div>
                  )}
                </div>
              )}

              {/* DOCUMENTS */}

              {preview.attachments?.length > 0 && (
                <div className="proforma-pdf-attachments">
                  <h4>Supporting Documents</h4>

                  <div className="proforma-pdf-document-list">
                    {preview.attachments.map((file, index) => (
                      <button
                        type="button"
                        key={file._id || index}
                        className="proforma-pdf-document-btn proforma-no-print"
                        onClick={() => openAttachment(file)}
                      >
                        📎 {getAttachmentName(file, index)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* FOOTER */}

              <div className="proforma-pdf-footer">
                <div>
                  <small>Purchase Proforma Invoice</small>
                </div>

                <div className="proforma-signature">
                  <div className="proforma-signature-line" />

                  <strong>Authorized Signatory</strong>

                  <span>Softvolute Buildcon LLP</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
